import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, EMPTY, exhaustMap, map, of, switchMap, filter, tap } from "rxjs";
import { TenderActions } from "./tender.actions";
import { TenderApiModel } from "@/core/models/api/tender-api-model.model";
import { TenderService } from "@/core/services/tender.service";
import { selectProjectIdFromRoute } from "./tender.selector";
import { Store } from "@ngrx/store";
@Injectable()
export class TenderEffects {
    actions$ = inject(Actions);
    tenderService = inject(TenderService);
    store = inject(Store);
    constructor() {

    }

    _loadTender = createEffect(() => {
        return this.actions$.pipe(
            ofType(TenderActions.loadTenders),
            exhaustMap(() => {
                return this.tenderService.getAll().pipe(
                    map((response: any) => {
                        const tenders = response as TenderApiModel[];
                        return TenderActions.loadTenderSuccess({ tenders });
                    }),
                    catchError((error) => {
                        console.error(error);
                        return of();
                    })
                )
            })
        )
    });

    _loadTenderById = createEffect(() => {
        return this.actions$.pipe(
            ofType(TenderActions.loadTenderById),
            exhaustMap((action) => {
                return this.tenderService.getById(action.tenderId).pipe(
                    map((response: any) => {
                        const tender = response as TenderApiModel;
                        return TenderActions.loadTenderByIdSuccess({ tender });
                    }),
                    catchError((error) => {
                        console.error(error);
                        return of();
                    })
                )
            })
        )
    });

    _loadTenderByIdAndSetCurrent = createEffect(() => {
        return this.actions$.pipe(
            ofType(TenderActions.loadByIdAndSetCurrent),
            exhaustMap((action) => {
                return this.tenderService.getById(action.tenderId).pipe(
                    map((response: any) => {
                        const tender = response as TenderApiModel;
                        return TenderActions.setCurrentTender({ tender });
                    }),
                    catchError((error) => {
                        console.error(error);
                        return of();
                    })
                )
            })
        )
    });

    _loadTenderByProjectId = createEffect(() => {
        return this.actions$.pipe(
            ofType(TenderActions.loadTenderByProjectId),
            exhaustMap((action) => {
                return this.tenderService.getLatestExecutionTenderByProjectId(action.projectId).pipe(
                    map((response: any) => {
                        const tender = response as TenderApiModel;
                        return TenderActions.setCurrentTender({ tender });
                    }),
                    catchError((error) => {
                        // 404 = el proyecto no tiene tender en "Ejecución". Estado esperado, no
                        // vale ensuciar la consola con él.
                        if (error?.status !== 404) {
                            console.error(error);
                        }
                        return of();
                    })
                )
            })
        )
    });

    syncExecutionWithRoute$ = createEffect(() =>
        this.store.select(selectProjectIdFromRoute).pipe(
            // Only proceed if the URL actually contains a projectId
            filter((projectId): projectId is string => !!projectId),
            tap((projectId) => console.log('Project ID from route:', projectId)), // Debug log to verify the projectId
            // Because we use store.select, it automatically applies distinctUntilChanged().
            // It will ONLY fire when the projectId actually changes!
            map(projectId => TenderActions.loadExecutionTenderByProjectId({ projectId: Number(projectId) }))
        )
    );

    loadExecution$ = createEffect(() => {
        return this.actions$.pipe(
            // 1. Listen for the action that starts the load
            ofType(TenderActions.loadExecutionTenderByProjectId),

            // 2. Use switchMap to handle the API call
            // It cancels any previous pending execution loads if a new one starts
            switchMap((action) =>
                this.tenderService.getLatestExecutionTenderByProjectId(action.projectId).pipe(

                    // 3. On Success: Dispatch the success action with the tender object
                    map((tender) =>
                        TenderActions.loadExecutionTenderByProjectIdSuccess({ tender })
                    ),

                    // 4. On Error: Dispatch the fail action so the loading spinner stops.
                    // 404 NO es un error: el backend responde NotFound cuando el proyecto no tiene
                    // ningún tender en estado "Ejecución". Ese caso se resuelve como éxito con
                    // tender = null para no dejar un errorMessage falso en el store.
                    catchError((error) =>
                        of(error?.status === 404
                            ? TenderActions.loadExecutionTenderByProjectIdSuccess({ tender: null })
                            : TenderActions.loadExecutionTenderByProjectIdFail({
                                error: error.message || 'Failed to load tender execution'
                            }))
                    )
                )
            )
        )
    });
}